import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {NGXLogger} from 'ngx-logger';
import {UserService} from './user.service';
import {ProjectService} from './project.service';
import { ENVIRONMENT } from '../../../models/src/lib/injection.tokens';
import { Order, OrderCount, Packaging } from '../../../models/src/lib/order';
import { Pageable } from '../../../models/src/lib/base';
import { User } from '../../../models/src/lib/user';
import { Project } from '../../../models/src/lib/project';
import { ShippedBoxDescription } from '../../../models/src/lib/box';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: "root",
})
export class OrderService {
  constructor(
    private userService: UserService,
    private projectService: ProjectService,
    protected httpClient: HttpClient,
    private logger: NGXLogger,
    private alertSvc: AlertService,
    @Inject(ENVIRONMENT) private environment: any
  ) {}

  getRecentOrders(options?: { cursor?: string; before?: boolean; pageSize?: number }) {
    const url = `${this.environment.apiHost}/ecom/customer/orders`;
    const params: { [key: string]: string } = {
      pageSize: String(options?.pageSize ?? 5),
    };
    if (options?.cursor) {
      params["cursor"] = options.cursor;
      params["before"] = String(options.before ?? false);
    }

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url, { params }).subscribe(
        (response) => {
          response = this.processOrders(response);
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
  processOrders(response: any) {
    response.orders.forEach(
      (order: {
        [x: string]: any;
        noteAttributes: any[];
        showProof: any;
        showShipped: any;
        showEstimatedShipping: any;
        delivery: any;
        received: any;
        isReceived: any;
        proofed: any;
        isProofed: any;
        proofApproved: any;
        isProofApproved: any;
        paymentCaptured: any;
        isPaymentCaptured: any;
        shipped: any;
        isShipped: any;
        completed: any;
        isCompleted: any;
        refunded: any;
        isRefunded: any;
        hasTracking: any;
        isDraft: any;
      }) => {
        order.noteAttributes.forEach((noteAttribute: { name: string | number; value: any }) => {
          order[noteAttribute.name] = noteAttribute.value;
        });

        order.showProof = this.showProof(order);
        order.showShipped = this.showShipped(order);
        order.showEstimatedShipping = this.showEstimatedShipping(order);
        order.delivery = this.getDelivery(order);
        order.received = this.getReceived(order);
        order.isReceived = this.isReceived(order);
        order.proofed = this.getProofed(order);
        order.isProofed = this.isProofed(order);
        order.proofApproved = this.getProofApproved(order);
        order.isProofApproved = this.isProofApproved(order);
        order.paymentCaptured = this.getPaymentCaptured(order);
        order.isPaymentCaptured = this.isPaymentCaptured(order);
        order.shipped = this.getShipped(order);
        order.isShipped = this.isShipped(order);
        order.completed = this.getCompleted(order);
        order.isCompleted = this.isCompleted(order);
        order.refunded = this.getRefunded(order);
        order.isRefunded = this.isRefunded(order);
        order.hasTracking = this.hasTracking(order);
        order.isDraft = this.isDraft(order);
      }
    );
    return response;
  }

  filterOrders(response: any) {
    if (response.orders) {
      const orders: any[] = [];
      response.orders.forEach((order: { isCompleted: any; isRefunded: any }) => {
        if (!order.isCompleted && !order.isRefunded) {
          orders.push(order);
        }
      });
      if (orders.length > 0) {
        response.orders = orders;
      } else if (response.orders.length > 3) {
        response.orders = response.orders.splice(0, 3);
      }
    }
    return response;
  }
  showProof(order: any) {
    const key = "requiresProofing";
    return order.properties[key];
  }
  showShipped(order: any) {
    const key = "requiresShipping";
    return order.properties[key];
  }
  isDraft(order:any) {
    const key = "draft";
    return order.properties[key];
  }

  showEstimatedShipping(order:any) {
    return !this.isRefunded(order) && this.showShipped(order) && !this.isShipped(order) && !this.isCompleted(order);
  }

  getDelivery(order:any) {
    const key = "DELIVERY";
    const delivery = order[key];
    if (delivery) {
      return new Date(delivery);
    }
    return undefined;
  }

  getReceived(order:any) {
    const status = this.getStatus(order, "RECEIVED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }

  isReceived(order:any) {
    return order.status.hasOwnProperty("RECEIVED");
  }

  getProofed(order:any) {
    const status = this.getStatus(order, "PROOFED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }
  isProofed(order:any) {
    return order.status.hasOwnProperty("PROOFED");
  }

  getProofApproved(order:any) {
    const status = this.getStatus(order, "PROOF_APPROVED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }

  isProofApproved(order:any) {
    return order.status.hasOwnProperty("PROOF_APPROVED");
  }

  getPaymentCaptured(order:any) {
    const status = this.getStatus(order, "PAYMENT_CAPTURED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }
  isPaymentCaptured(order:any) {
    return order.status.hasOwnProperty("PAYMENT_CAPTURED");
  }

  getShipped(order:any) {
    const status = this.getStatus(order, "SHIPPED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }
  isShipped(order:any) {
    return order.status.hasOwnProperty("SHIPPED");
  }

  getCompleted(order:any) {
    const status = this.getStatus(order, "COMPLETED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }
  isCompleted(order:any) {
    return order.status.hasOwnProperty("COMPLETED");
  }

  getRefunded(order:any) {
    const status = this.getStatus(order, "REFUNDED");
    if (status) {
      return new Date(status);
    }
    return undefined;
  }
  isRefunded(order:any) {
    return order.status.hasOwnProperty("REFUNDED");
  }

  getStatus(order:any, name:string) {
    const status = order.status[name];
    if (status && status != null) {
      return status;
    }
    return "";
  }

  hasTracking(order:any) {
    let tracking = false;
    if (order.fulfillments) {
      order.fulfillments.forEach((fulfillment:any) => {
        if (fulfillment.status === "success" && fulfillment.tracking_number) {
          tracking = true;
        }
      });
    }
    return tracking;
  }

  getUserOrder(userId: number, orderId: string | number): Promise<Order> {
    return new Promise<Order>((resolve, reject) => {
      const url = `${this.environment.apiHost}/ecom/orders/${userId}/${orderId}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          try {
            resolve(new Order().deserialize(res));
          } catch (err) {
            console.error("getUserOrder: deserialize failed", { url, response: res, err });
            reject(err);
          }
        },
        (error) => {
          console.error("getUserOrder: request failed", { url, error });
          reject(error);
        }
      );
    });
  }

  getOrder(orderId: number, decorateOrder?: boolean, decorateLineItems?: boolean): Promise<Order> {
    return new Promise<Order>((resolve, reject) => {
      const url = `${this.environment.apiHost}/ecom/orders/${orderId}`;
      return this.httpClient.get(url).subscribe(
        (res) => {
          const order = new Order().deserialize(res);
          this.logger.debug("order", order);
          if (decorateOrder === undefined || decorateOrder) {
            this.decorateOrder(order).then(
              () => {
                if (decorateLineItems === undefined || decorateLineItems) {
                  this.decorateLineItems(order).then(
                    () => {
                      resolve(order);
                    },
                    () => {
                      resolve(order);
                    }
                  );
                } else {
                  resolve(order);
                }
              },
              () => {
                reject(order);
              }
            );
          } else if (decorateLineItems === undefined || decorateLineItems) {
            this.decorateLineItems(order).then(
              () => {
                resolve(order);
              },
              () => {
                resolve(order);
              }
            );
          } else {
            resolve(order);
          }
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getOrderDataUrl(orderName: string): string {
    return `${this.environment.apiHost}/project/order/${orderName}.json`;
  }

  listAllOrders(
    page: string,
    pageSize: number,
    orderStatus: string,
    financialStatus: string,
    fulfillmentStatus: any,
    loadUser: boolean = true
  ): Promise<Pageable<Order>> {
    // this.logger.debug('Loading projects');
    let url = `${this.environment.apiHost}/ecom/orders/list?`;

    url += `pageInfo=${page}&limit=${pageSize}&`;

    if (orderStatus) {
      url += `orderStatus=${orderStatus}&`;
    }
    if (financialStatus) {
      url += `financialStatus=${financialStatus}&`;
    }
    if (fulfillmentStatus) {
      url += `fulfillmentStatus=${fulfillmentStatus}&`;
    }
    return this.internalGetOrders(url, loadUser);
  }

  listUserOrders(
    userId: number,
    page: number,
    pageSize: number,
    orderStatus: string | undefined,
    financialStatus: string | undefined,
    fulfillmentStatus: any,
    loadUser: boolean = false
  ): Promise<Pageable<Order>> {
    // this.logger.debug('Loading projects');
    let url = `${this.environment.apiHost}/ecom/orders/${userId}/list?`;
    if (page > 0) {
      const offset = page - 1;
      url += `offset=${offset}&limit=${pageSize}&`;
    }
    if (orderStatus) {
      url += `orderStatus=${orderStatus}&`;
    }
    if (financialStatus) {
      url += `financialStatus=${financialStatus}&`;
    }
    if (fulfillmentStatus) {
      url += `fulfillmentStatus=${fulfillmentStatus}&`;
    }
    return this.internalGetOrders(url, loadUser);
  }

  private createGuestUser(order: Order) {
    if (!order.billingAddress) {
      return new User();
    }
    return new User().deserialize({
      /*
                  'firstName': order.billingAddress ? order.billingAddress.firstName : '',
                  'lastName': order.billingAddress ? order.billingAddress.lastName : '',
                  'contactEmail': order.billingAddress.contactEmail,
                  'shopifyId': order.customer && order.customer.id ? order.customer.id : 0,
                  'guest': true
            */
    });
  }

  search(orderNumber: string): Promise<Order> {
    return new Promise((resolve, reject) => {
      if (!orderNumber.toUpperCase().startsWith(this.environment.orderPrefix)) {
        orderNumber = this.environment.orderPrefix + orderNumber;
      }
      const url = `${this.environment.apiHost}/ecom/orders/search?name=${orderNumber}`;
      return this.httpClient.get(url).subscribe(
        (res: any) => {
          if (res && res.id) {
            const order = new Order().deserialize(res);
            this.logger.debug("order", order);
            this.decorateOrder(order).then(
              () => {
                this.decorateLineItems(order).then(
                  () => {
                    resolve(order);
                  },
                  () => {
                    resolve(order);
                  }
                );
              },
              (reason) => {
                reject(reason);
              }
            );
          } else {
            resolve(new Order());
          }
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  updateNoteAttributes(order: Order): Promise<Order> {
    return new Promise((resolve, reject) => {
      this.logger.debug("Updating order", order);

      const url = `${this.environment.apiHost}/ecom/orders/${order.id}.json`;
      return this.httpClient
        .put(url, {
          id: order.id,
          note_attributes: order.noteAttributes,
        })
        .subscribe(
          (res: any) => {
            const newOrder = new Order().deserialize(res);
            this.logger.debug("order", newOrder);
            this.decorateOrder(newOrder)
              .then(() => {
                this.decorateLineItems(newOrder).then(
                  () => {
                    resolve(newOrder);
                  },
                  () => {
                    resolve(newOrder);
                  }
                );
              })
              .catch(() => {
                resolve(newOrder);
              });
          },
          (error) => {
            this.logger.debug(error);
            reject(error);
          }
        );
    });
  }

  private internalGetOrders(url: string, loadUser: boolean = true): Promise<Pageable<Order>> {
    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          const list: Order[] = [];
          (response.content as any[]).forEach((object) => {
            const order: Order = new Order().deserialize(object);
            if (loadUser && order.customer && !order.user) {
              this.userService
                .getUserByExternalId(order.customer?.id ?? "")
                .then((res) => {
                  order.user = new User().deserialize(res);
                })
                .catch(() => {
                  order.user = User.EMPTY;
                });
            } else {
              order.user = this.createGuestUser(order);
              // console.log(order.user);
            }
            list.push(order);
          });
          const page: Pageable<Order> = new Pageable<Order>().deserialize(response);
          page.content = list;
          this.logger.debug("Loaded orders", page);
          resolve(page);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  private decorateOrder(order: Order): Promise<void> {
    return new Promise((resolve, _reject) => {
      if (order.customer) {
        this.userService
          .getUserByExternalId(order.customer?.id ?? "")
          .then((res) => {
            order.user = new User().deserialize(res);
            resolve();
          })
          .catch(() => {
            this.alertSvc.showWarning({ msg: "Unable to load customer" });
            order.user = User.EMPTY;
            resolve();
          });
      } else {
        order.user = this.createGuestUser(order);
        resolve();
      }
    });
  }

  private decorateLineItems(order: Order): Promise<boolean> {
    const promises: any[] = [];
    this.userService.getCurrentUser().then((user: { id: any }) => {
      for (const lineItem of order.lineItems) {
        // console.log('lineItem', lineItem);
        lineItem.containerName = lineItem.properties.getValue("container_name");
        if (lineItem.containerName && !lineItem.title?.includes("Spring") && !lineItem.title?.includes("No")) {
          promises.push(
              this.projectService.getProjectByContainer(user.id, lineItem.containerName, false, false).then(
                (value) => {
                  this.logger.debug("project", value);
                  lineItem.project = value;
                },
                () => {
                  this.alertSvc.showWarning({
                    msg: "Unable to load project for container" + lineItem.containerName,
                  });
                }
              )
          );
        } else {
          lineItem.project = Project.EMPTY;
        }
      }
    })

    return new Promise<boolean>((resolve) => {
      Promise.all(promises).then(
        () => {
          resolve(true);
        },
        () => {
          resolve(true);
        }
      );
    });
  }

  package(boxes: ShippedBoxDescription[]): Promise<Packaging> {
    const url = `${this.environment.apiHost}/ecom/packaging/`;
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(url, {
          boxes,
        })
        .subscribe(
          (response) => {
            const packaging = new Packaging().deserialize(response);
            this.logger.debug("Loaded packaging", packaging);
            if (packaging.weight === Infinity) {
              reject("Calculation returned Infinity");
            } else {
              resolve(packaging);
            }
          },
          (error) => {
            this.logger.debug(error);
            reject(error);
          }
        );
    });
  }

  count(): Promise<OrderCount> {
    const url = `${this.environment.apiHost}/ecom/orders/counts`;
    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response) => {
          const orderCount = new OrderCount().deserialize(response);
          this.logger.trace("Got order count", orderCount);
          resolve(orderCount);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  resendOrderData(orderName: string): Promise<boolean> {
    const url = `${this.environment.apiHost}/project/order/${orderName}.json`;
    return new Promise((resolve, reject) => {
      this.httpClient.post(url, {}).subscribe(
        (response: any) => {
          if (response.success) {
            resolve(true);
          } else {
            this.logger.debug("Failed to send order data");
            reject();
          }
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  resumeOrder(order: Order): Promise<boolean> {
    const url = `${this.environment.apiHost}/project/order/${order.name}/resume`;
    const containerNames = order.lineItems
      .filter((value) => value.project)
      .map((value) => value.project)
      .map((value) => value?.container);
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(url, {
          containerNames,
        })
        .subscribe(
          (response: any) => {
            if (response.success) {
              resolve(true);
            } else {
              this.logger.debug("Failed to send order data");
              reject();
            }
          },
          (error) => {
            this.logger.debug(error);
            reject(error);
          }
        );
    });
  }
}
